import React, { ReactElement, useState } from 'react';
import { useDispatch, useSelector } from '@ecommerce/nest-ssr/common';

import Histogram from './components/histogram';
import RangeSliderMulti, { Range } from 'atomic/range-slider-multi';
import Input from 'atomic/form/input';

import { CategoryStateInterface } from 'modules/categories/interfaces/category-state.interface';
import { useCategoryUrl } from 'modules/categories/hooks/use-category-url';
import { FiltersActions } from 'modules/categories/reducers/category-url-params.reducer';

import './filter-price.scss';

const FilterPrice: React.FC = (): ReactElement | null => {
    const aggregations = useSelector((state: CategoryStateInterface) => state.aggregations);
    const price = useSelector((state: CategoryStateInterface) => state.categoryUrlParams.price);
    const getUrl = useCategoryUrl();
    const dispatch = useDispatch();
    const [values, setValues] = useState<Range>({ min: price?.min || 0, max: price?.max || aggregations.maxPrice });

    const enter = (event: React.KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === 'Enter') {
            window.location.replace(getUrl({ page: 1, price: calculatePriceRange() }));
        }
    };

    const handleChange = (value: Range): void => {
        setValues(value);
    };

    const handleChangeComplete = (value: Range): void => {
        setValues({ min: checkValue('min', value.min), max: checkValue('max', value.max) });

        dispatch({ type: FiltersActions.SET_PRICE, price: calculatePriceRange() });
    };

    const calculatePriceRange = (): Range => {
        const price: Range = { min: Math.min(values.min, values.max), max: Math.max(values.min, values.max) };

        if (values.min !== aggregations.minPrice) {
            price.min = Math.min(values.min, values.max);
        } else {
            price.min = 0;
        }

        if (values.max !== aggregations.maxPrice) {
            price.max = Math.max(values.min, values.max);
        } else {
            price.max = 0;
        }

        return price;
    };

    const checkValue = (key: string, value: number): number => {
        if (key === 'min') {
            if (value < 0) {
                return 0;
            } else if (value > values.max || value > aggregations.maxPrice) {
                return values.max - 1;
            }
        }

        if (key === 'max') {
            if (value > aggregations.maxPrice) {
                return aggregations.maxPrice;
            } else if (value <= values.min) {
                return values.min + 1;
            }
        }

        return value;
    };

    const onChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, key: string): void => {
        const value = checkValue(key, parseInt(event.target.value));
        setValues({ ...values, [key]: value });
        dispatch({ type: FiltersActions.SET_PRICE, price: { ...values, [key]: value } });
    };

    return (
        <div className="filter-price">
            <div className="filter-price__range">
                <Input name="priceMin"
                    type="number"
                    min={0}
                    max={aggregations.maxPrice}
                    label="Od"
                    small={true}
                    value={values.min === 0 ? '' : values.min}
                    onChange={(event): void => onChange(event, 'min')}
                    onKeyPress={enter}
                />
                <span className="filter-price__range-separator">-</span>
                <Input name="priceMax"
                    type="number"
                    min={0}
                    max={aggregations.maxPrice}
                    label="Do"
                    small={true}
                    value={values.max}
                    onChange={(event): void => onChange(event, 'max')}
                    onKeyPress={enter}
                />
            </div>
            <Histogram aggregations={aggregations} min={values.min} max={values.max} />
            <RangeSliderMulti
                valuesRange={{ min: values.min, max: values.max }}
                minValue={0}
                maxValue={aggregations.maxPrice}
                onChangeComplete={handleChangeComplete}
                onChange={handleChange}
                ariaLabelledby="Cena"
            />
        </div>
    );
};

export default FilterPrice;
